/**
 * SchemaField - JSON Schema editor
 *
 * Provides a visual builder for JSON Schema definitions with property list UI.
 * Supports expanded mode (property list) and collapsed mode (JSON text).
 * Includes "Generate from Sample" feature to infer schema from sample JSON.
 */

import { useState, useCallback, useRef, useEffect, useMemo, type ReactNode } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  Label,
  Text,
  Button,
  Dropdown,
  Option,
  Checkbox,
  Input,
  Tooltip,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@coreai/fluentui-react';
import {
  AddFilled,
  DeleteFilled,
  CodeFilled,
  TextFieldFilled,
  SparkleRegular,
  ChevronRightFilled,
  ChevronDownFilled,
} from '@fluentui/react-icons';
import { EditorState, Compartment } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, placeholder } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { json } from '@codemirror/lang-json';
import { bracketMatching, foldGutter, foldKeymap } from '@codemirror/language';
import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { search, searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { useAppStore } from '@/stores/appStore';
import { createFluentTheme, formatJsonKeymap, wrapSelectionInputHandler } from '@/components/WorkflowDesigner/CodeEditor/extensions';
import type { ValueSegment } from '@/types/valueSegment';
import {
  generateSegmentId,
  ValueSegmentType,
  isValueSegmentArray,
  segmentsToString,
} from '@/types/valueSegment';

import {
  fieldContainerStyle,
  fieldLabelRowStyle,
  fieldLabelStyle,
  fieldRequiredStyle,
} from './fieldStyles';

const useStyles = makeStyles({
  container: fieldContainerStyle,
  labelRow: fieldLabelRowStyle,
  label: fieldLabelStyle,
  required: fieldRequiredStyle,
  modeToggle: {
    display: 'flex',
    gap: tokens.spacingHorizontalXXS,
  },
  propertyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  propertyHeader: {
    display: 'grid',
    gridTemplateColumns: '24px 1fr 120px 80px 32px',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXS,
  },
  headerLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
  },
  propertyRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  propertyRowMain: {
    display: 'grid',
    gridTemplateColumns: '24px 1fr 120px 80px 32px',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
  propertyRowMainNoExpand: {
    display: 'grid',
    gridTemplateColumns: '1fr 120px 80px 32px',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    marginLeft: '32px', // token-exception: structural — 24px expand control + 8px grid gap
  },
  expandButton: {
    minWidth: 'auto',
    width: '24px',
    height: '24px',
    padding: 0,
  },
  expandPlaceholder: {
    width: '24px',
  },
  propertyName: {
    flex: 1,
  },
  nestedProperties: {
    marginLeft: '32px', // token-exception: structural — aligns with the field rows above
    paddingLeft: tokens.spacingHorizontalM,
    borderLeft: `2px solid ${tokens.colorNeutralStroke2}`,
  },
  nestedHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalXS,
  },
  nestedLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  addNestedButton: {
    minWidth: 'auto',
    height: '24px',
    padding: `0 ${tokens.spacingHorizontalS}`,
    fontSize: tokens.fontSizeBase200,
  },
  itemsTypeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginLeft: '56px',
    marginBottom: tokens.spacingVerticalXS,
  },
  itemsTypeLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  itemsTypeSelector: {
    minWidth: '120px',
  },
  rootTypeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  rootTypeLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  rootTypeSelector: {
    minWidth: '100px',
  },
  typeSelector: {
    minWidth: '120px',
  },
  requiredCheckbox: {
    justifySelf: 'center',
  },
  deleteButton: {
    minWidth: 'auto',
    height: '32px',
    padding: tokens.spacingHorizontalS,
  },
  addButtons: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXS,
  },
  addButtonsCentered: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
    justifyContent: 'center',
  },
  emptyState: {
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    fontStyle: 'italic',
  },
  error: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  sampleEditorContainer: {
    marginTop: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    overflow: 'hidden',
    minHeight: '200px',
    maxHeight: '400px',
    '& .cm-editor': {
      height: '100%',
      minHeight: '200px',
      maxHeight: '400px',
    },
    '& .cm-scroller': {
      fontFamily: tokens.fontFamilyMonospace,
      fontSize: tokens.fontSizeBase300,
      overflow: 'auto',
    },
  },
  dialogError: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalS,
  },
  jsonEditorContainer: {
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    overflow: 'hidden',
    minHeight: '150px',
    maxHeight: '400px',
    '& .cm-editor': {
      height: '100%',
      minHeight: '150px',
      maxHeight: '400px',
    },
    '& .cm-scroller': {
      fontFamily: tokens.fontFamilyMonospace,
      fontSize: tokens.fontSizeBase300,
      overflow: 'auto',
    },
  },
});

// Schema property types
export type SchemaType = 'string' | 'number' | 'integer' | 'boolean' | 'object' | 'array' | 'null';

const SCHEMA_TYPES: SchemaType[] = ['string', 'number', 'integer', 'boolean', 'object', 'array', 'null'];

export interface SchemaProperty {
  id: string;
  name: string;
  /** Type can be a single type or array of types */
  type: SchemaType | SchemaType[];
  required: boolean;
  description?: string | undefined;
  /** Nested properties for object type */
  properties?: SchemaProperty[] | undefined;
  /** Item schema for array type */
  items?: { type: SchemaType | SchemaType[]; properties?: SchemaProperty[] } | undefined;
}

/** JSON Schema with nested support */
interface JSONSchema {
  type: string | string[];
  properties?: Record<string, JSONSchema>;
  required?: string[];
  items?: JSONSchema;
  description?: string;
}

export interface SchemaFieldProps {
  name: string;
  label: string;
  value: string | ValueSegment[];
  onChange: (value: string | ValueSegment[]) => void;
  description?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  /** Allowed types (defaults to all) */
  allowedTypes?: SchemaType[] | undefined;
  /**
   * Optional slot rendered in the label row alongside the visual / JSON
   * mode toggle. Hosts inline affordances such as `FieldActionChips`.
   */
  endActions?: ReactNode | undefined;
}

/**
 * Normalize type to array format for internal use
 */
function normalizeType(type: string | string[] | undefined): SchemaType | SchemaType[] {
  if (!type) return 'string';
  if (Array.isArray(type)) {
    return type as SchemaType[];
  }
  return type as SchemaType;
}

/**
 * Check if type includes a specific value
 */
function typeIncludes(type: SchemaType | SchemaType[], check: SchemaType): boolean {
  if (Array.isArray(type)) {
    return type.includes(check);
  }
  return type === check;
}

/**
 * Parse JSON Schema into SchemaProperty[] recursively
 */
function parseSchemaObject(schema: JSONSchema, requiredSet: Set<string>): SchemaProperty[] {
  if (!schema.properties) {
    return [];
  }

  return Object.entries(schema.properties).map(([name, prop]) => {
    const propType = normalizeType(prop.type);
    const property: SchemaProperty = {
      id: generateSegmentId(),
      name,
      type: propType,
      required: requiredSet.has(name),
      description: prop.description,
    };

    // Handle nested object properties
    if (typeIncludes(propType, 'object') && prop.properties) {
      const nestedRequired = new Set(prop.required || []);
      property.properties = parseSchemaObject(prop, nestedRequired);
    }

    // Handle array items
    if (typeIncludes(propType, 'array') && prop.items) {
      const itemType = normalizeType(prop.items.type);
      property.items = {
        type: itemType,
      };
      if (typeIncludes(itemType, 'object') && prop.items.properties) {
        const itemsRequired = new Set(prop.items.required || []);
        property.items.properties = parseSchemaObject(prop.items, itemsRequired);
      }
    }

    return property;
  });
}

/** Root schema type - either object with properties or array with items */
export type RootSchemaType = 'object' | 'array';

export interface ParsedSchema {
  rootType: RootSchemaType;
  properties: SchemaProperty[];
  /** For array root type, the type of items */
  itemsType?: SchemaType | SchemaType[];
  /** For array of array, the type of nested items */
  nestedItemsType?: SchemaType | SchemaType[];
}

/**
 * Parse JSON Schema string into SchemaProperty[] with root type info
 * @internal Exported for testing
 */
export function parseSchema(value: string | ValueSegment[]): ParsedSchema {
  const stringValue = isValueSegmentArray(value)
    ? segmentsToString(value)
    : value;

  if (!stringValue || stringValue.trim() === '') {
    return { rootType: 'object', properties: [] };
  }

  try {
    const schema = JSON.parse(stringValue) as JSONSchema;
    
    // Handle array root type
    if (schema.type === 'array' && schema.items) {
      const itemsType = normalizeType(schema.items.type);
      
      // Handle nested array (array of arrays)
      if (typeIncludes(itemsType, 'array') && schema.items.items) {
        const nestedItemsType = normalizeType(schema.items.items.type);
        if (typeIncludes(nestedItemsType, 'object') && schema.items.items.properties) {
          const requiredSet = new Set(schema.items.items.required || []);
          return {
            rootType: 'array',
            properties: parseSchemaObject(schema.items.items, requiredSet),
            itemsType,
            nestedItemsType,
          };
        }
        return { rootType: 'array', properties: [], itemsType, nestedItemsType };
      }
      
      if (typeIncludes(itemsType, 'object') && schema.items.properties) {
        const requiredSet = new Set(schema.items.required || []);
        return {
          rootType: 'array',
          properties: parseSchemaObject(schema.items, requiredSet),
          itemsType,
        };
      }
      return { rootType: 'array', properties: [], itemsType };
    }
    
    // Handle object root type
    if (schema.type === 'object' && schema.properties) {
      const requiredSet = new Set(schema.required || []);
      return {
        rootType: 'object',
        properties: parseSchemaObject(schema, requiredSet),
      };
    }
    
    return { rootType: 'object', properties: [] };
  } catch {
    return { rootType: 'object', properties: [] };
  }
}

/**
 * Serialize SchemaProperty[] to JSON Schema recursively
 */
function serializePropertyToSchema(prop: SchemaProperty): JSONSchema {
  const schema: JSONSchema = {
    type: prop.type,
    ...(prop.description ? { description: prop.description } : {}),
  };

  // Handle nested object properties
  if (typeIncludes(prop.type, 'object') && prop.properties && prop.properties.length > 0) {
    schema.properties = {};
    const required: string[] = [];

    for (const nestedProp of prop.properties) {
      schema.properties[nestedProp.name] = serializePropertyToSchema(nestedProp);
      if (nestedProp.required) {
        required.push(nestedProp.name);
      }
    }

    if (required.length > 0) {
      schema.required = required;
    }
  }

  // Handle array items
  if (typeIncludes(prop.type, 'array') && prop.items) {
    schema.items = { type: prop.items.type };
    if (typeIncludes(prop.items.type, 'object') && prop.items.properties && prop.items.properties.length > 0) {
      schema.items.properties = {};
      const required: string[] = [];

      for (const itemProp of prop.items.properties) {
        schema.items.properties[itemProp.name] = serializePropertyToSchema(itemProp);
        if (itemProp.required) {
          required.push(itemProp.name);
        }
      }

      if (required.length > 0) {
        schema.items.required = required;
      }
    }
  }

  return schema;
}

/**
 * Serialize SchemaProperty[] to JSON Schema string
 * @param properties - The properties to serialize
 * @param rootType - Whether root is 'object' or 'array'
 * @param itemsType - For array root, the type of items (defaults to 'object')
 * @param nestedItemsType - For array of arrays, the type of nested items
 * @internal Exported for testing
 */
export function serializeSchema(
  properties: SchemaProperty[],
  rootType: RootSchemaType = 'object',
  itemsType: SchemaType | SchemaType[] = 'object',
  nestedItemsType?: SchemaType | SchemaType[]
): string {
  if (rootType === 'array') {
    // Handle nested array (array of arrays)
    if (typeIncludes(itemsType, 'array') && nestedItemsType) {
      const schema: JSONSchema = {
        type: 'array',
        items: {
          type: 'array',
          items: {
            type: nestedItemsType,
          },
        },
      };

      // Only add properties if nested items type includes object
      if (typeIncludes(nestedItemsType, 'object') && properties.length > 0) {
        schema.items!.items!.properties = {};
        const required: string[] = [];

        for (const prop of properties) {
          schema.items!.items!.properties[prop.name] = serializePropertyToSchema(prop);
          if (prop.required) {
            required.push(prop.name);
          }
        }

        if (required.length > 0) {
          schema.items!.items!.required = required;
        }
      }

      return JSON.stringify(schema, null, 2);
    }

    // Array root type - items contain the properties
    const schema: JSONSchema = {
      type: 'array',
      items: {
        type: itemsType,
      },
    };

    // Only add properties if items type includes object
    if (typeIncludes(itemsType, 'object') && properties.length > 0) {
      schema.items!.properties = {};
      const required: string[] = [];

      for (const prop of properties) {
        schema.items!.properties[prop.name] = serializePropertyToSchema(prop);
        if (prop.required) {
          required.push(prop.name);
        }
      }

      if (required.length > 0) {
        schema.items!.required = required;
      }
    }

    return JSON.stringify(schema, null, 2);
  }

  // Object root type (default)
  const schema: JSONSchema = {
    type: 'object',
    properties: {},
    required: [],
  };

  for (const prop of properties) {
    schema.properties![prop.name] = serializePropertyToSchema(prop);
    if (prop.required) {
      schema.required!.push(prop.name);
    }
  }

  // Remove empty required array
  if (schema.required!.length === 0) {
    delete schema.required;
  }

  return JSON.stringify(schema, null, 2);
}

/**
 * Infer type from a value
 * @internal Exported for testing
 */
export function inferType(value: unknown): SchemaType {
  if (value === null) return 'string';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') {
    return Number.isInteger(value) ? 'integer' : 'number';
  }
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object') return 'object';
  return 'string';
}

export interface GeneratedSchema {
  rootType: RootSchemaType;
  properties: SchemaProperty[];
  itemsType?: SchemaType;
}

/**
 * Generate schema properties from an object sample
 */
function generatePropertiesFromObject(sample: Record<string, unknown>): SchemaProperty[] {
  return Object.entries(sample).map(([key, value]) => {
    const type = inferType(value);
    const property: SchemaProperty = {
      id: generateSegmentId(),
      name: key,
      type,
      required: true,
    };

    // Recursively handle nested objects
    if (type === 'object' && typeof value === 'object' && value !== null && !Array.isArray(value)) {
      property.properties = generatePropertiesFromObject(value as Record<string, unknown>);
    }

    // Handle arrays - infer item schema from first element
    if (type === 'array' && Array.isArray(value) && value.length > 0) {
      const firstItem = value[0];
      const itemType = inferType(firstItem);
      property.items = { type: itemType };

      // If array items are objects, recursively get their properties
      if (itemType === 'object' && typeof firstItem === 'object' && firstItem !== null) {
        property.items.properties = generatePropertiesFromObject(firstItem as Record<string, unknown>);
      }
    }

    return property;
  });
}

/**
 * Generate schema from sample JSON - supports both object and array samples
 * @internal Exported for testing
 */
export function generateSchemaFromSample(sample: unknown): GeneratedSchema {
  // Handle array at root level
  if (Array.isArray(sample)) {
    if (sample.length === 0) {
      // Empty array - default to object items
      return { rootType: 'array', properties: [], itemsType: 'object' };
    }
    
    const firstItem = sample[0];
    const itemsType = inferType(firstItem);
    
    // If items are objects, extract their properties
    if (itemsType === 'object' && typeof firstItem === 'object' && firstItem !== null) {
      return {
        rootType: 'array',
        properties: generatePropertiesFromObject(firstItem as Record<string, unknown>),
        itemsType: 'object',
      };
    }
    
    // Primitive array items
    return { rootType: 'array', properties: [], itemsType };
  }
  
  // Handle object at root level
  if (typeof sample === 'object' && sample !== null) {
    return {
      rootType: 'object',
      properties: generatePropertiesFromObject(sample as Record<string, unknown>),
    };
  }
  
  // Primitive value - treat as empty object schema
  return { rootType: 'object', properties: [] };
}

export const SchemaField = ({
  name,
  label,
  value,
  onChange,
  description,
  required,
  disabled = false,
  error,
  allowedTypes,
  endActions,
}: SchemaFieldProps) => {
  const styles = useStyles();
  const theme = useAppStore((s) => s.theme);

  // Parse schema once on mount - derive all initial state from this
  // External value changes are synced via useEffect below
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialParsed = useMemo(() => parseSchema(value), []);

  // Track expanded vs collapsed mode
  const [isExpanded, setIsExpanded] = useState(true);
  // Track which property nodes are expanded (for nested properties)
  const [expandedProps, setExpandedProps] = useState<Set<string>>(new Set());
  // Root schema type (object or array)
  const [rootType, setRootType] = useState<RootSchemaType>(initialParsed.rootType);
  // For array root, the items type
  const [arrayItemsType, setArrayItemsType] = useState<SchemaType>(
    (initialParsed.itemsType as SchemaType) || 'object'
  );
  // For array of arrays, the nested items type
  const [nestedArrayItemsType, setNestedArrayItemsType] = useState<SchemaType>(
    (initialParsed.nestedItemsType as SchemaType) || 'object'
  );
  // Internal properties state - maintains stable IDs across edits
  const [properties, setProperties] = useState<SchemaProperty[]>(initialParsed.properties);
  // Generate from sample dialog
  const [showSampleDialog, setShowSampleDialog] = useState(false);
  const [sampleJson, setSampleJson] = useState('');
  const [sampleError, setSampleError] = useState('');

  // CodeMirror editor for sample JSON
  const sampleEditorContainerRef = useRef<HTMLDivElement>(null);
  const sampleEditorViewRef = useRef<EditorView | null>(null);
  const sampleThemeCompartment = useRef(new Compartment());
  const sampleJsonRef = useRef(sampleJson);
  sampleJsonRef.current = sampleJson;

  // CodeMirror editor for JSON mode
  const jsonEditorContainerRef = useRef<HTMLDivElement>(null);
  const jsonEditorViewRef = useRef<EditorView | null>(null);
  const jsonThemeCompartment = useRef(new Compartment());
  const jsonValueRef = useRef<string>('');
  const isJsonInternalChange = useRef(false);
  const jsonOnChangeRef = useRef(onChange);
  jsonOnChangeRef.current = onChange;
  const jsonInitialValueRef = useRef<string | ValueSegment[]>(value);
  // Keep initial value ref updated so re-mounts (e.g. toggling modes) pick up the latest value,
  // but don't trigger the setup effect on every keystroke.
  jsonInitialValueRef.current = value;

  // Set up CodeMirror editor when dialog opens
  useEffect(() => {
    if (!showSampleDialog || !sampleEditorContainerRef.current) return;

    // Clean up existing editor
    if (sampleEditorViewRef.current) {
      sampleEditorViewRef.current.destroy();
      sampleEditorViewRef.current = null;
    }

    const state = EditorState.create({
      doc: sampleJsonRef.current,
      extensions: [
        lineNumbers(),
        history(),
        foldGutter({
          openText: '▼',
          closedText: '▶',
        }),
        bracketMatching(),
        wrapSelectionInputHandler(),
        closeBrackets(),
        highlightSelectionMatches(),
        search({ top: true }),
        EditorState.tabSize.of(2),
        keymap.of([
          ...closeBracketsKeymap,
          ...defaultKeymap,
          ...historyKeymap,
          ...foldKeymap,
          ...searchKeymap,
          ...formatJsonKeymap,
          indentWithTab,
        ]),
        json(),
        sampleThemeCompartment.current.of(createFluentTheme(theme)),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            setSampleJson(update.state.doc.toString());
          }
        }),
      ],
    });

    const view = new EditorView({
      state,
      parent: sampleEditorContainerRef.current,
    });

    sampleEditorViewRef.current = view;

    return () => {
      view.destroy();
      sampleEditorViewRef.current = null;
    };
  }, [showSampleDialog, theme]);

  // Update theme when it changes (sample editor)
  useEffect(() => {
    if (!sampleEditorViewRef.current) return;
    sampleEditorViewRef.current.dispatch({
      effects: sampleThemeCompartment.current.reconfigure(createFluentTheme(theme)),
    });
  }, [theme]);

  // Set up CodeMirror editor for JSON mode
  useEffect(() => {
    if (isExpanded || !jsonEditorContainerRef.current) {
      // Clean up editor when switching to visual mode
      if (jsonEditorViewRef.current) {
        jsonEditorViewRef.current.destroy();
        jsonEditorViewRef.current = null;
      }
      return;
    }

    // Get current value (read from ref so this effect doesn't re-run on every change)
    const initialValue = jsonInitialValueRef.current;
    const currentValue = isValueSegmentArray(initialValue)
      ? segmentsToString(initialValue)
      : (initialValue || '{}');
    jsonValueRef.current = currentValue;

    const state = EditorState.create({
      doc: currentValue,
      extensions: [
        lineNumbers(),
        history(),
        foldGutter({
          openText: '▼',
          closedText: '▶',
        }),
        bracketMatching(),
        wrapSelectionInputHandler(),
        closeBrackets(),
        highlightSelectionMatches(),
        search({ top: true }),
        EditorState.tabSize.of(2),
        keymap.of([
          ...closeBracketsKeymap,
          ...defaultKeymap,
          ...historyKeymap,
          ...foldKeymap,
          ...searchKeymap,
          ...formatJsonKeymap,
          indentWithTab,
        ]),
        json(),
        ...(description
          ? [
              placeholder(() => {
                const el = document.createElement('div');
                el.textContent = description;
                el.style.whiteSpace = 'pre-wrap';
                el.style.opacity = '0.6';
                el.style.pointerEvents = 'none';
                // Constrain to zero height so multi-line placeholder text doesn't
                // stretch the first line (and the cursor) to span the full block.
                el.style.display = 'inline-block';
                el.style.height = '0';
                el.style.overflow = 'visible';
                el.style.verticalAlign = 'top';
                return el;
              }),
            ]
          : []),
        jsonThemeCompartment.current.of(createFluentTheme(theme)),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            const newValue = update.state.doc.toString();
            jsonValueRef.current = newValue;
            isJsonInternalChange.current = true;
            jsonOnChangeRef.current([{
              id: generateSegmentId(),
              type: ValueSegmentType.LITERAL,
              value: newValue,
            }]);
          }
        }),
      ],
    });

    const view = new EditorView({
      state,
      parent: jsonEditorContainerRef.current,
    });

    jsonEditorViewRef.current = view;

    return () => {
      view.destroy();
      jsonEditorViewRef.current = null;
    };
  }, [isExpanded, theme, description]);

  // Update JSON editor theme when it changes
  useEffect(() => {
    if (!jsonEditorViewRef.current) return;
    jsonEditorViewRef.current.dispatch({
      effects: jsonThemeCompartment.current.reconfigure(createFluentTheme(theme)),
    });
  }, [theme]);

  // Sync external value changes to JSON editor
  useEffect(() => {
    if (isExpanded || !jsonEditorViewRef.current) return;
    if (isJsonInternalChange.current) {
      isJsonInternalChange.current = false;
      return;
    }

    const currentValue = isValueSegmentArray(value)
      ? segmentsToString(value)
      : (value || '{}');

    if (currentValue !== jsonValueRef.current) {
      jsonValueRef.current = currentValue;
      jsonEditorViewRef.current.dispatch({
        changes: {
          from: 0,
          to: jsonEditorViewRef.current.state.doc.length,
          insert: currentValue,
        },
      });
    }
  }, [isExpanded, value]);

  // Track if we initiated the change (to avoid re-parsing our own updates)
  const isInternalChangeRef = useRef(false);

  // Track the last serialized value we sent out
  const lastSerializedRef = useRef<string>(serializeSchema(properties, rootType, arrayItemsType, nestedArrayItemsType));

  // Sync from external value changes only
  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }

    const stringValue = isValueSegmentArray(value)
      ? segmentsToString(value)
      : value;

    // Only re-parse if the external value is different from what we last sent
    if (stringValue !== lastSerializedRef.current) {
      const parsed = parseSchema(value);
      setProperties(parsed.properties);
      setRootType(parsed.rootType);
      if (parsed.itemsType) {
        setArrayItemsType(parsed.itemsType as SchemaType);
      }
      if (parsed.nestedItemsType) {
        setNestedArrayItemsType(parsed.nestedItemsType as SchemaType);
      }
      lastSerializedRef.current = stringValue || '{}';
    }
  }, [value]);

  // Toggle expand state for a property
  const toggleExpand = useCallback((propId: string) => {
    setExpandedProps((prev) => {
      const next = new Set(prev);
      if (next.has(propId)) {
        next.delete(propId);
      } else {
        next.add(propId);
      }
      return next;
    });
  }, []);

  // Available types
  const availableTypes = allowedTypes || SCHEMA_TYPES;


  // Update properties and notify onChange
  const updateProperties = useCallback((newProperties: SchemaProperty[], newRootType?: RootSchemaType, newItemsType?: SchemaType, newNestedItemsType?: SchemaType) => {
    const effectiveRootType = newRootType ?? rootType;
    const effectiveItemsType = newItemsType ?? arrayItemsType;
    const effectiveNestedItemsType = newNestedItemsType ?? nestedArrayItemsType;
    const serialized = serializeSchema(newProperties, effectiveRootType, effectiveItemsType, effectiveNestedItemsType);
    lastSerializedRef.current = serialized;
    isInternalChangeRef.current = true;
    setProperties(newProperties);
    if (newRootType !== undefined) {
      setRootType(newRootType);
    }
    if (newItemsType !== undefined) {
      setArrayItemsType(newItemsType);
    }
    if (newNestedItemsType !== undefined) {
      setNestedArrayItemsType(newNestedItemsType);
    }
    onChange([{
      id: generateSegmentId(),
      type: ValueSegmentType.LITERAL,
      value: serialized,
    }]);
  }, [onChange, rootType, arrayItemsType, nestedArrayItemsType]);

  /**
   * Recursively update a property in the tree by path
   * Path is an array of property IDs leading to the target
   * Special marker '__items__' indicates we should traverse into array items
   */
  const updatePropertyInTree = useCallback((
    props: SchemaProperty[],
    path: string[],
    updater: (prop: SchemaProperty) => SchemaProperty | null
  ): SchemaProperty[] => {
    if (path.length === 0) return props;

    const [currentId, ...restPath] = path;

    if (restPath.length === 0) {
      // We're at the target property
      return props
        .map((prop) => prop.id === currentId ? updater(prop) : prop)
        .filter((prop): prop is SchemaProperty => prop !== null);
    }

    // Need to go deeper
    return props.map((prop) => {
      if (prop.id !== currentId) return prop;

      // Check if next path is __items__ (array items)
      if (restPath[0] === '__items__' && prop.items?.properties) {
        return {
          ...prop,
          items: {
            ...prop.items,
            properties: updatePropertyInTree(prop.items.properties, restPath.slice(1), updater),
          },
        };
      }

      // Check if we need to update nested properties
      if (prop.properties) {
        return {
          ...prop,
          properties: updatePropertyInTree(prop.properties, restPath, updater),
        };
      }

      return prop;
    });
  }, []);

  // Handle property name change
  const handleNameChange = useCallback((path: string[], newName: string) => {
    const newProps = updatePropertyInTree(properties, path, (prop) => ({
      ...prop,
      name: newName,
    }));
    updateProperties(newProps);
  }, [properties, updateProperties, updatePropertyInTree]);

  // Handle property type change (supports multi-select for primitives)
  const handleTypeChange = useCallback((path: string[], selectedTypes: SchemaType[]) => {
    if (selectedTypes.length === 0) return; // Must have at least one type

    const newProps = updatePropertyInTree(properties, path, (prop) => {
      // If only one type selected, use single value; otherwise use array
      const newType: SchemaType | SchemaType[] = selectedTypes.length === 1
        ? selectedTypes[0]!
        : selectedTypes;
      const updated: SchemaProperty = { ...prop, type: newType };

      // Initialize nested structures when changing to object/array
      if (typeIncludes(newType, 'object') && !updated.properties) {
        updated.properties = [];
      }
      if (typeIncludes(newType, 'array') && !updated.items) {
        updated.items = { type: 'string' };
      }
      // Clear nested structures when changing away from object/array
      if (!typeIncludes(newType, 'object')) {
        delete updated.properties;
      }
      if (!typeIncludes(newType, 'array')) {
        delete updated.items;
      }
      return updated;
    });
    updateProperties(newProps);
  }, [properties, updateProperties, updatePropertyInTree]);

  // Handle property required change
  const handleRequiredChange = useCallback((path: string[], isRequired: boolean) => {
    const newProps = updatePropertyInTree(properties, path, (prop) => ({
      ...prop,
      required: isRequired,
    }));
    updateProperties(newProps);
  }, [properties, updateProperties, updatePropertyInTree]);

  // Handle remove property
  const handleRemoveProperty = useCallback((path: string[]) => {
    const newProps = updatePropertyInTree(properties, path, () => null);
    updateProperties(newProps);
  }, [properties, updateProperties, updatePropertyInTree]);

  // Handle add property at a path (empty path = top level)
  const handleAddProperty = useCallback((parentPath: string[] = []) => {
    const newProp: SchemaProperty = {
      id: generateSegmentId(),
      name: 'newProperty',
      type: 'string',
      required: false,
    };

    if (parentPath.length === 0) {
      // Add to top level
      updateProperties([...properties, newProp]);
    } else {
      // Add to nested object
      const newProps = updatePropertyInTree(properties, parentPath, (prop) => ({
        ...prop,
        properties: [...(prop.properties || []), newProp],
      }));
      updateProperties(newProps);
    }
  }, [properties, updateProperties, updatePropertyInTree]);

  // Handle add item property (for array items that are objects)
  const handleAddItemProperty = useCallback((arrayPath: string[]) => {
    const newProp: SchemaProperty = {
      id: generateSegmentId(),
      name: 'newProperty',
      type: 'string',
      required: false,
    };

    const newProps = updatePropertyInTree(properties, arrayPath, (prop) => ({
      ...prop,
      items: {
        ...prop.items!,
        properties: [...(prop.items?.properties || []), newProp],
      },
    }));
    updateProperties(newProps);
  }, [properties, updateProperties, updatePropertyInTree]);

  // Handle array items type change
  const handleItemsTypeChange = useCallback((path: string[], newType: SchemaType) => {
    const newProps = updatePropertyInTree(properties, path, (prop) => {
      const newItems = { type: newType } as SchemaProperty['items'];
      if (newType === 'object') {
        newItems!.properties = prop.items?.properties || [];
      }
      return { ...prop, items: newItems };
    });
    updateProperties(newProps);
  }, [properties, updateProperties, updatePropertyInTree]);

  // Handle root type change
  const handleRootTypeChange = useCallback((newRootType: RootSchemaType) => {
    updateProperties(properties, newRootType, arrayItemsType, nestedArrayItemsType);
  }, [properties, arrayItemsType, nestedArrayItemsType, updateProperties]);

  // Handle array items type change (for array root)
  const handleArrayItemsTypeChange = useCallback((newItemsType: SchemaType) => {
    // When changing away from object items type, clear properties
    // When changing to array, reset nested items type to 'object'
    const newProperties = (newItemsType === 'object' || newItemsType === 'array') ? properties : [];
    const newNestedItemsType = newItemsType === 'array' ? nestedArrayItemsType : undefined;
    updateProperties(newProperties, rootType, newItemsType, newNestedItemsType);
  }, [properties, rootType, nestedArrayItemsType, updateProperties]);

  // Handle nested array items type change (for array of arrays)
  const handleNestedArrayItemsTypeChange = useCallback((newNestedItemsType: SchemaType) => {
    // When changing away from object items type, clear properties
    const newProperties = newNestedItemsType === 'object' ? properties : [];
    updateProperties(newProperties, rootType, arrayItemsType, newNestedItemsType);
  }, [properties, rootType, arrayItemsType, updateProperties]);

  // Handle generate from sample
  const handleGenerateFromSample = useCallback(() => {
    setSampleError('');
    try {
      const parsed = JSON.parse(sampleJson);
      const inferred = generateSchemaFromSample(parsed);
      
      // Check if we got any meaningful schema
      if (inferred.rootType === 'object' && inferred.properties.length === 0) {
        setSampleError('Sample must be a non-empty object or array');
        return;
      }
      if (inferred.rootType === 'array' && inferred.itemsType === 'object' && inferred.properties.length === 0) {
        // Array of empty objects is still valid - just no properties to show
      }
      
      updateProperties(inferred.properties, inferred.rootType, inferred.itemsType);
      // Auto-expand all generated properties
      const collectIds = (props: SchemaProperty[]): string[] => {
        const ids: string[] = [];
        for (const prop of props) {
          if (prop.type === 'object' || prop.type === 'array') {
            ids.push(prop.id);
          }
          if (prop.properties) {
            ids.push(...collectIds(prop.properties));
          }
          if (prop.items?.properties) {
            ids.push(...collectIds(prop.items.properties));
          }
        }
        return ids;
      };
      setExpandedProps(new Set(collectIds(inferred.properties)));
      setShowSampleDialog(false);
      setSampleJson('');
    } catch {
      setSampleError('Invalid JSON. Please enter valid JSON.');
    }
  }, [sampleJson, updateProperties]);


  /**
   * Get display text for type (handles array of types)
   */
  const getTypeDisplayText = (type: SchemaType | SchemaType[]): string => {
    if (Array.isArray(type)) {
      return type.join(', ');
    }
    return type;
  };

  /**
   * Get selected types as array
   */
  const getSelectedTypes = (type: SchemaType | SchemaType[]): SchemaType[] => {
    if (Array.isArray(type)) {
      return type;
    }
    return [type];
  };

  /**
   * Render a single property row with its nested content
   */
  const renderPropertyRow = (prop: SchemaProperty, path: string[], depth: number = 0) => {
    const hasNestedContent = typeIncludes(prop.type, 'object') || typeIncludes(prop.type, 'array');
    const isPropExpanded = expandedProps.has(prop.id);
    const currentPath = [...path, prop.id];
    const selectedTypes = getSelectedTypes(prop.type);

    return (
      <div key={prop.id} className={styles.propertyRow}>
        {/* Main row */}
        <div className={hasNestedContent ? styles.propertyRowMain : styles.propertyRowMainNoExpand}>
          {hasNestedContent && (
            <Button
              appearance="subtle"
              size="small"
              icon={isPropExpanded ? <ChevronDownFilled /> : <ChevronRightFilled />}
              onClick={() => toggleExpand(prop.id)}
              className={styles.expandButton}
              aria-label={isPropExpanded ? 'Collapse' : 'Expand'}
              disabled={disabled}
            />
          )}
          <Input
            className={styles.propertyName}
            size="small"
            value={prop.name}
            onChange={(_e, data) => handleNameChange(currentPath, data.value)}
            disabled={disabled}
            placeholder="Property name"
          />
          <Dropdown
            className={styles.typeSelector}
            size="small"
            value={getTypeDisplayText(prop.type)}
            selectedOptions={selectedTypes}
            onOptionSelect={(_e, data) => {
              // Toggle the selected type
              const clickedType = data.optionValue as SchemaType;
              let newSelection: SchemaType[];

              if (selectedTypes.includes(clickedType)) {
                // Removing - but keep at least one type
                newSelection = selectedTypes.filter(t => t !== clickedType);
                if (newSelection.length === 0) {
                  newSelection = [clickedType]; // Can't remove last type
                }
              } else {
                // Adding - but object and array are exclusive (can't combine with others)
                if (clickedType === 'object' || clickedType === 'array') {
                  newSelection = [clickedType];
                } else if (selectedTypes.includes('object') || selectedTypes.includes('array')) {
                  // Switching from object/array to primitive
                  newSelection = [clickedType];
                } else {
                  // Adding another primitive type
                  newSelection = [...selectedTypes, clickedType];
                }
              }
              handleTypeChange(currentPath, newSelection);
            }}
            multiselect
            disabled={disabled}
            aria-label="Property type"
          >
            {availableTypes.map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Dropdown>
          <Checkbox
            className={styles.requiredCheckbox}
            checked={prop.required}
            onChange={(_e, data) =>
              handleRequiredChange(currentPath, data.checked === true)
            }
            disabled={disabled}
            aria-label="Required"
          />
          <Tooltip content="Remove property" relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={<DeleteFilled />}
              onClick={() => handleRemoveProperty(currentPath)}
              disabled={disabled}
              className={styles.deleteButton}
              aria-label="Remove property"
            />
          </Tooltip>
        </div>

        {/* Nested content for objects */}
        {typeIncludes(prop.type, 'object') && isPropExpanded && (
          <div className={styles.nestedProperties}>
            <div className={styles.nestedHeader}>
              <Text className={styles.nestedLabel}>Object properties:</Text>
              <Button
                appearance="subtle"
                size="small"
                icon={<AddFilled />}
                onClick={() => handleAddProperty(currentPath)}
                disabled={disabled}
                className={styles.addNestedButton}
              >
                Add
              </Button>
            </div>
            {(!prop.properties || prop.properties.length === 0) ? (
              <Text className={styles.nestedLabel}>No properties defined</Text>
            ) : (
              prop.properties.map((nestedProp) =>
                renderPropertyRow(nestedProp, currentPath, depth + 1)
              )
            )}
          </div>
        )}

        {/* Nested content for arrays */}
        {typeIncludes(prop.type, 'array') && isPropExpanded && (
          <>
            {/* Array items type selector */}
            <div className={styles.itemsTypeRow}>
              <Text className={styles.itemsTypeLabel}>Items type:</Text>
              <Dropdown
                className={styles.itemsTypeSelector}
                size="small"
                value={getTypeDisplayText(prop.items?.type ?? 'string')}
                selectedOptions={getSelectedTypes(prop.items?.type ?? 'string')}
                onOptionSelect={(_e, data) =>
                  handleItemsTypeChange(currentPath, data.optionValue as SchemaType)
                }
                disabled={disabled}
                aria-label="Array items type"
              >
                {availableTypes.map((type) => (
                  <Option key={type} value={type}>
                    {type}
                  </Option>
                ))}
              </Dropdown>
            </div>

            {/* If items are objects, show their properties */}
            {prop.items && typeIncludes(prop.items.type ?? 'string', 'object') && (
              <div className={styles.nestedProperties}>
                <div className={styles.nestedHeader}>
                  <Text className={styles.nestedLabel}>Item properties:</Text>
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<AddFilled />}
                    onClick={() => handleAddItemProperty(currentPath)}
                    disabled={disabled}
                    className={styles.addNestedButton}
                  >
                    Add
                  </Button>
                </div>
                {(!prop.items.properties || prop.items.properties.length === 0) ? (
                  <Text className={styles.nestedLabel}>No properties defined</Text>
                ) : (
                  prop.items.properties.map((itemProp) =>
                    renderPropertyRow(itemProp, [...currentPath, '__items__'], depth + 1)
                  )
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.labelRow}>
        <Label className={styles.label} htmlFor={name}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </Label>
        <div className={styles.modeToggle}>
          {endActions}
          <Tooltip content="Visual editor" relationship="label">
            <Button
              appearance={isExpanded ? 'primary' : 'subtle'}
              size="small"
              icon={<TextFieldFilled />}
              onClick={() => setIsExpanded(true)}
              disabled={disabled}
              aria-label="Visual editor"
            />
          </Tooltip>
          <Tooltip content="Edit as JSON" relationship="label">
            <Button
              appearance={!isExpanded ? 'primary' : 'subtle'}
              size="small"
              icon={<CodeFilled />}
              onClick={() => setIsExpanded(false)}
              disabled={disabled}
              aria-label="Edit as JSON"
            />
          </Tooltip>
        </div>
      </div>

      {isExpanded ? (
        // Expanded mode - property list
        <div className={styles.propertyList}>
          {/* Root type selector */}
          <div className={styles.rootTypeRow}>
            <Text className={styles.rootTypeLabel}>Schema type:</Text>
            <Dropdown
              className={styles.rootTypeSelector}
              size="small"
              value={rootType === 'array' ? 'Array' : 'Object'}
              selectedOptions={[rootType]}
              onOptionSelect={(_e, data) => handleRootTypeChange(data.optionValue as RootSchemaType)}
              disabled={disabled}
              aria-label="Root schema type"
            >
              <Option value="object">Object</Option>
              <Option value="array">Array</Option>
            </Dropdown>
            {rootType === 'array' && (
              <>
                <Text className={styles.rootTypeLabel}>of</Text>
                <Dropdown
                  className={styles.rootTypeSelector}
                  size="small"
                  value={arrayItemsType}
                  selectedOptions={[arrayItemsType]}
                  onOptionSelect={(_e, data) => handleArrayItemsTypeChange(data.optionValue as SchemaType)}
                  disabled={disabled}
                  aria-label="Array items type"
                >
                  {availableTypes.filter(t => t !== 'null').map((type) => (
                    <Option key={type} value={type}>
                      {type}
                    </Option>
                  ))}
                </Dropdown>
                {arrayItemsType === 'array' && (
                  <>
                    <Text className={styles.rootTypeLabel}>of</Text>
                    <Dropdown
                      className={styles.rootTypeSelector}
                      size="small"
                      value={nestedArrayItemsType}
                      selectedOptions={[nestedArrayItemsType]}
                      onOptionSelect={(_e, data) => handleNestedArrayItemsTypeChange(data.optionValue as SchemaType)}
                      disabled={disabled}
                      aria-label="Nested array items type"
                    >
                      {availableTypes.filter(t => t !== 'null' && t !== 'array').map((type) => (
                        <Option key={type} value={type}>
                          {type}
                        </Option>
                      ))}
                    </Dropdown>
                  </>
                )}
              </>
            )}
          </div>
          
          {/* Only show properties editor for object root, array of objects, or array of array of objects */}
          {(rootType === 'object' || (rootType === 'array' && arrayItemsType === 'object') || (rootType === 'array' && arrayItemsType === 'array' && nestedArrayItemsType === 'object')) ? (
            <>
              {properties.length === 0 ? (
                <div className={styles.emptyState}>
                  No properties defined. Add a property or generate from sample JSON.
                </div>
              ) : (
                <>
                  <div className={styles.propertyHeader}>
                    <div /> {/* Spacer for expand button */}
                    <Text className={styles.headerLabel}>Property Name</Text>
                    <Text className={styles.headerLabel}>Type</Text>
                    <Text className={styles.headerLabel}>Required</Text>
                    <div /> {/* Spacer for delete button */}
                  </div>
                  {properties.map((prop) => renderPropertyRow(prop, []))}
                </>
              )}
              <div className={styles.addButtons}>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<AddFilled />}
                  onClick={() => handleAddProperty([])}
                  disabled={disabled}
                >
                  Add Property
                </Button>
            <Dialog
              open={showSampleDialog}
              onOpenChange={(_, data) => {
                setShowSampleDialog(data.open);
                if (!data.open) {
                  // Reset state when dialog closes
                  setSampleJson('');
                  setSampleError('');
                }
              }}
            >
              <DialogTrigger disableButtonEnhancement>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<SparkleRegular />}
                  disabled={disabled}
                >
                  Generate from Sample
                </Button>
              </DialogTrigger>
              <DialogSurface>
                <DialogBody>
                  <DialogTitle>Generate Schema from Sample JSON</DialogTitle>
                  <DialogContent>
                    <Text>
                      Paste a sample JSON object or array below. The schema will be inferred from its structure.
                    </Text>
                    <div
                      ref={sampleEditorContainerRef}
                      className={styles.sampleEditorContainer}
                    />
                    {sampleError && (
                      <Text className={styles.dialogError}>{sampleError}</Text>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <DialogTrigger disableButtonEnhancement>
                      <Button appearance="secondary">Cancel</Button>
                    </DialogTrigger>
                    <Button
                      appearance="primary"
                      onClick={handleGenerateFromSample}
                      disabled={!sampleJson.trim()}
                    >
                      Generate
                    </Button>
                  </DialogActions>
                </DialogBody>
              </DialogSurface>
            </Dialog>
              </div>
            </>
          ) : (
            // Array of primitive type - no properties to edit
            <div className={styles.emptyState}>
              {arrayItemsType === 'array' 
                ? `Array of array of ${nestedArrayItemsType} values.`
                : `Array of ${arrayItemsType} values.`
              } Use &quot;Generate from Sample&quot; to infer schema from sample data.
              <div className={styles.addButtonsCentered}>
                <Dialog
                  open={showSampleDialog}
                  onOpenChange={(_, data) => {
                    setShowSampleDialog(data.open);
                    if (!data.open) {
                      setSampleJson('');
                      setSampleError('');
                    }
                  }}
                >
                  <DialogTrigger disableButtonEnhancement>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<SparkleRegular />}
                      disabled={disabled}
                    >
                      Generate from Sample
                    </Button>
                  </DialogTrigger>
                  <DialogSurface>
                    <DialogBody>
                      <DialogTitle>Generate Schema from Sample JSON</DialogTitle>
                      <DialogContent>
                        <Text>
                          Paste a sample JSON object or array below. The schema will be inferred from its structure.
                        </Text>
                        <div
                          ref={sampleEditorContainerRef}
                          className={styles.sampleEditorContainer}
                        />
                        {sampleError && (
                          <Text className={styles.dialogError}>{sampleError}</Text>
                        )}
                      </DialogContent>
                      <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                          <Button appearance="secondary">Cancel</Button>
                        </DialogTrigger>
                        <Button
                          appearance="primary"
                          onClick={handleGenerateFromSample}
                          disabled={!sampleJson.trim()}
                        >
                          Generate
                        </Button>
                      </DialogActions>
                    </DialogBody>
                  </DialogSurface>
                </Dialog>
              </div>
            </div>
          )}
        </div>
      ) : (
        // Collapsed mode - CodeMirror JSON editor
        <div
          ref={jsonEditorContainerRef}
          className={styles.jsonEditorContainer}
        />
      )}

      {error && (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      )}
    </div>
  );
};

SchemaField.displayName = 'SchemaField';
